import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TicketModal } from '@/components/support/TicketModal';
import { requestPasswordReset, validatePasswordReset, confirmPasswordReset } from '@/services/passwordReset';
import { getErrorMessage } from '@/utils/errorHandler';


function generateDeviceFingerprint(): string {
  const nav = window.navigator;
  const raw = [
    nav.userAgent,
    nav.language,
    screen.width,
    screen.height,
    new Date().getTimezoneOffset(),
  ].join('|');
  let hash = 0;
  for (let i = 0; i < raw.length; i++) {
    hash = (hash << 5) - hash + raw.charCodeAt(i);
    hash |= 0;
  }
  return `WEB-${Math.abs(hash).toString(16).toUpperCase()}`;
}

type Etapa = 1 | 2 | 3;

export default function RecuperarSenhaPage() {
  const navigate = useNavigate();
  const [etapa, setEtapa] = useState<Etapa>(1);

  // Etapa 1
  const [tipoDoc, setTipoDoc] = useState<'cpf' | 'cnpj'>('cpf');
  const [login, setLogin] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [canalEnvio, setCanalEnvio] = useState<'email' | 'sms'>('email');

  // Etapa 2 — Token
  const [token, setToken] = useState(['', '', '', '', '', '']);
  const [tokenError, setTokenError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [tentativas, setTentativas] = useState(0);
  const [bloqueado, setBloqueado] = useState(false);
  const [bloqueioCountdown, setBloqueioCountdown] = useState(0);
  const MAX_TENTATIVAS = 3;
  const BLOQUEIO_SEGUNDOS = 120;
  const tokenRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Etapa 3 — Nova senha
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [showNovaSenha, setShowNovaSenha] = useState(false);
  const [showConfirmarSenha, setShowConfirmarSenha] = useState(false);
  const [errosSenha, setErrosSenha] = useState<{ novaSenha?: string; confirmarSenha?: string }>({});
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [showSemAcessoInfo, setShowSemAcessoInfo] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [apiError, setApiError] = useState('');
  const [apiErrorDetails, setApiErrorDetails] = useState<string[]>([]);

  // Contato mascarado (vindo da API)
  const [contatoMascarado, setContatoMascarado] = useState({ email: '', celular: '' });

  // Cooldown reenvio
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // Countdown de bloqueio
  useEffect(() => {
    if (bloqueioCountdown <= 0) return;
    const timer = setTimeout(() => {
      setBloqueioCountdown((c) => {
        if (c - 1 <= 0) {
          setBloqueado(false);
          setTentativas(0);
          setToken(['', '', '', '', '', '']);
          setTokenError('');
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearTimeout(timer);
  }, [bloqueioCountdown]);

  const formatBloqueioTime = (secs: number) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // ─── Helpers ────────────────────────────────────────────────────────────────

  const formatDoc = (value: string, tipo: 'cpf' | 'cnpj'): string => {
    if (tipo === 'cpf') {
      const d = value.replace(/\D/g, '').slice(0, 11);
      return d.replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    const d = value.replace(/\D/g, '').slice(0, 14);
    return d.replace(/(\d{2})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1/$2').replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  };

  const validateCPF = (cpf: string): boolean => {
    if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;
    let sum = 0;
    for (let i = 0; i < 9; i++) sum += parseInt(cpf[i]) * (10 - i);
    let d = 11 - (sum % 11); if (d >= 10) d = 0;
    if (d !== parseInt(cpf[9])) return false;
    sum = 0;
    for (let i = 0; i < 10; i++) sum += parseInt(cpf[i]) * (11 - i);
    d = 11 - (sum % 11); if (d >= 10) d = 0;
    return d === parseInt(cpf[10]);
  };

  const validateCNPJ = (cnpj: string): boolean => {
    if (cnpj.length !== 14 || /^(\d)\1{13}$/.test(cnpj)) return false;
    let size = 12; let numbers = cnpj.substring(0, size); const digits = cnpj.substring(size);
    let sum = 0; let pos = size - 7;
    for (let i = size; i >= 1; i--) { sum += parseInt(numbers[size - i]) * pos--; if (pos < 2) pos = 9; }
    let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    if (result !== parseInt(digits[0])) return false;
    size = 13; numbers = cnpj.substring(0, size); sum = 0; pos = size - 7;
    for (let i = size; i >= 1; i--) { sum += parseInt(numbers[size - i]) * pos--; if (pos < 2) pos = 9; }
    result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    return result === parseInt(digits[1]);
  };

  const validateLogin = (): boolean => {
    if (!login.trim()) { setError('Por favor, informe seu CPF ou CNPJ'); return false; }
    const digits = login.replace(/\D/g, '');
    const expected = tipoDoc === 'cpf' ? 11 : 14;
    if (digits.length !== expected) { setError(tipoDoc === 'cpf' ? 'CPF incompleto (11 dígitos)' : 'CNPJ incompleto (14 dígitos)'); return false; }
    if (tipoDoc === 'cpf' && !validateCPF(digits)) { setError('CPF inválido'); return false; }
    if (tipoDoc === 'cnpj' && !validateCNPJ(digits)) { setError('CNPJ inválido'); return false; }
    return true;
  };

  const calcularForca = (s: string) => {
    if (!s) return { nivel: 0, label: '', cor: '' };
    let score = 0;
    if (s.length >= 8) score++;
    if (s.length >= 12) score++;
    if (/[A-Z]/.test(s)) score++;
    if (/[0-9]/.test(s)) score++;
    if (/[^A-Za-z0-9]/.test(s)) score++;
    if (score <= 1) return { nivel: 1, label: 'Muito fraca', cor: 'bg-red-500' };
    if (score === 2) return { nivel: 2, label: 'Fraca', cor: 'bg-orange-400' };
    if (score === 3) return { nivel: 3, label: 'Média', cor: 'bg-yellow-400' };
    if (score === 4) return { nivel: 4, label: 'Forte', cor: 'bg-green-400' };
    return { nivel: 5, label: 'Muito forte', cor: 'bg-green-600' };
  };

  const forcaSenha = calcularForca(novaSenha);

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleTipoDocChange = (tipo: 'cpf' | 'cnpj') => { setTipoDoc(tipo); setLogin(''); setError(''); setApiError(''); setApiErrorDetails([]); };

  const handleLoginChange = (value: string) => { setLogin(formatDoc(value, tipoDoc)); setError(''); setApiError(''); setApiErrorDetails([]); };

  const handleSubmitEtapa1 = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateLogin()) return;
    setIsLoading(true);
    setApiError('');
    setApiErrorDetails([]);

    try {
      const data = await requestPasswordReset({
        login: login.replace(/\D/g, ''),
        canal: canalEnvio,
        deviceFingerprint: generateDeviceFingerprint(),
      });

      if (data?.data?.emailMascarado || data?.data?.celularMascarado) {
        setContatoMascarado({
          email: data.data.emailMascarado ?? '',
          celular: data.data.celularMascarado ?? '',
        });
      }
      setResendCooldown(60);
      setTentativas(0);
      setTokenError('');
      setToken(['', '', '', '', '', '']);
      setEtapa(2);
    } catch (err) {
      const msg = getErrorMessage(err);
      setApiError(msg);
      setApiErrorDetails([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Token input handlers
  const handleTokenChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const newToken = [...token];
    newToken[index] = digit;
    setToken(newToken);
    setTokenError('');
    if (digit && index < 5) {
      tokenRefs.current[index + 1]?.focus();
    }
  };

  const handleTokenKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !token[index] && index > 0) {
      tokenRefs.current[index - 1]?.focus();
    }
  };

  const handleTokenPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setToken(pasted.split(''));
      tokenRefs.current[5]?.focus();
    }
  };

  const handleVerifyToken = async (e: React.FormEvent) => {
    e.preventDefault();
    if (bloqueado) return;
    const code = token.join('');
    if (code.length < 6) { setTokenError('Digite o código completo de 6 dígitos'); return; }
    setIsVerifying(true);
    setApiError('');
    setApiErrorDetails([]);

    try {
      const data = await validatePasswordReset({
        login: login.replace(/\D/g, ''),
        code,
        deviceFingerprint: generateDeviceFingerprint(),
      });

      if (data?.data?.resetToken) {
        setTentativas(0);
        setResetToken(data.data.resetToken);
        setEtapa(3);
      }
    } catch {
      const novasTentativas = tentativas + 1;
      setTentativas(novasTentativas);
      setToken(['', '', '', '', '', '']);

      if (novasTentativas >= MAX_TENTATIVAS) {
        setBloqueado(true);
        setBloqueioCountdown(BLOQUEIO_SEGUNDOS);
        setTokenError('');
      } else {
        const restantes = MAX_TENTATIVAS - novasTentativas;
        const baseMsg = 'Código inválido. Verifique e tente novamente.';
        setTokenError(
          restantes === 1
            ? `${baseMsg} Atenção: esta é sua última tentativa!`
            : `${baseMsg} Você ainda tem ${restantes} tentativa${restantes > 1 ? 's' : ''}.`
        );
        tokenRefs.current[0]?.focus();
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setToken(['', '', '', '', '', '']);
    setTokenError('');
    setApiError('');
    setApiErrorDetails([]);

    try {
      const data = await requestPasswordReset({
        login: login.replace(/\D/g, ''),
        canal: canalEnvio,
        deviceFingerprint: generateDeviceFingerprint(),
      });

      if (data?.data?.emailMascarado || data?.data?.celularMascarado) {
        setContatoMascarado({
          email: data.data.emailMascarado ?? contatoMascarado.email,
          celular: data.data.celularMascarado ?? contatoMascarado.celular,
        });
      }
      setResendCooldown(60);
    } catch (err) {
      const msg = getErrorMessage(err);
      setTokenError(msg);
    }
  };

  const handleSubmitEtapa3 = async (e: React.FormEvent) => {
    e.preventDefault();
    const erros: { novaSenha?: string; confirmarSenha?: string } = {};
    if (!novaSenha) erros.novaSenha = 'Campo obrigatório';
    else if (novaSenha.length < 8) erros.novaSenha = 'Mínimo 8 caracteres';
    else if (!/[A-Z]/.test(novaSenha)) erros.novaSenha = 'Inclua ao menos uma letra maiúscula';
    else if (!/[0-9]/.test(novaSenha)) erros.novaSenha = 'Inclua ao menos um número';
    if (!confirmarSenha) erros.confirmarSenha = 'Campo obrigatório';
    else if (novaSenha !== confirmarSenha) erros.confirmarSenha = 'As senhas não coincidem';
    setErrosSenha(erros);
    if (Object.keys(erros).length > 0) return;

    setIsSaving(true);
    setApiError('');
    setApiErrorDetails([]);

    try {
      await confirmPasswordReset({
        login: login.replace(/\D/g, ''),
        resetToken,
        novaSenha,
        confirmarSenha,
        deviceFingerprint: generateDeviceFingerprint(),
      });
      setShowSuccessModal(true);
    } catch (err) {
      const msg = getErrorMessage(err);
      setApiError(msg);
      setApiErrorDetails([]);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseModal = () => { setShowSuccessModal(false); navigate('/premiado/login'); };

  // ─── Render ─────────────────────────────────────────────────────────────────

  const stepLabels: { label: string; icon: string }[] = [
    { label: 'Identificação', icon: 'ri-id-card-line' },
    { label: 'Verificação', icon: 'ri-shield-keyhole-line' },
    { label: 'Nova Senha', icon: 'ri-lock-password-line' },
  ];

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex flex-col lg:flex-row">
        {/* Left Side */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-orange-500 via-orange-600 to-red-600 p-16 flex-col justify-between relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-white rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
          </div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/5 rounded-full -ml-48 -mb-48"></div>

          <div className="relative z-10">
            <div className="flex items-center gap-4 mb-12">
              <div className="w-14 h-14 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center shadow-lg">
                <i className="ri-lock-password-line text-3xl text-white"></i>
              </div>
              <div>
                <span className="text-2xl font-bold text-white block">Recuperar Senha</span>
                <span className="text-orange-100 text-sm">Redefina sua senha com segurança</span>
              </div>
            </div>
            <h1 className="text-5xl font-bold text-white mb-6 leading-tight">
              Esqueceu sua<br />senha? Sem<br />
              <span className="text-orange-200">problemas!</span>
            </h1>
            <p className="text-orange-50 text-lg leading-relaxed max-w-md">
              {etapa === 1 && 'Informe seu CPF ou CNPJ cadastrado e escolha como quer receber o código de verificação.'}
              {etapa === 2 && `Enviamos um código de verificação para o ${canalEnvio === 'email' ? 'e-mail' : 'celular'} cadastrado na sua conta.`}
              {etapa === 3 && 'Crie uma nova senha forte para proteger sua conta.'}
            </p>
          </div>

          <div className="relative z-10 grid grid-cols-1 gap-4">
            {[
              { icon: 'ri-shield-check-line', title: 'Processo Seguro', desc: 'Verificação em duas etapas com criptografia' },
              { icon: 'ri-mail-check-line', title: 'Token por E-mail/SMS', desc: 'Código enviado para seus contatos cadastrados' },
              { icon: 'ri-customer-service-line', title: 'Suporte Disponível', desc: 'Nossa equipe está pronta para ajudar' },
            ].map((item) => (
              <div key={item.title} className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/20 hover:bg-white/15 transition-all duration-300 group">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <i className={`${item.icon} text-2xl text-white`}></i>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg mb-1">{item.title}</h3>
                    <p className="text-orange-100 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Side */}
        <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-16 overflow-y-auto">
          <div className="w-full max-w-md py-6 sm:py-8">

            {/* Stepper */}
            <div className="flex items-center justify-center mb-6">
              {stepLabels.map((step, idx) => {
                const num = idx + 1;
                const isCompleted = etapa > num;
                const isActive = etapa === num;
                return (
                  <div key={step.label} className="flex items-center">
                    <div className="flex flex-col items-center gap-1">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                        isCompleted ? 'bg-green-500 text-white' : isActive ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-400'
                      }`}>
                        {isCompleted ? <i className="ri-check-line text-base"></i> : <i className={`${step.icon} text-base`}></i>}
                      </div>
                      <span className={`text-xs font-medium whitespace-nowrap ${isActive ? 'text-orange-600' : isCompleted ? 'text-green-600' : 'text-gray-400'}`}>
                        {step.label}
                      </span>
                    </div>
                    {idx < stepLabels.length - 1 && (
                      <div className={`w-10 h-0.5 mx-1 mb-4 transition-all ${etapa > num ? 'bg-green-400' : 'bg-gray-200'}`}></div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 lg:p-10 border border-gray-100">
              <div className="mb-6 sm:mb-8">
                <div className="flex justify-center mb-4 sm:mb-6">
                  <img
                    src="https://static.readdy.ai/image/22e92c18f0b031d861a5e53bf8e362fb/6356cf72c36f3ef424056fffdf6d6f31.png"
                    alt="GetPoints Logo"
                    className="h-10 sm:h-12 w-auto"
                  />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-800 mb-2 text-center">
                  {etapa === 1 && 'Recuperar Senha'}
                  {etapa === 2 && 'Verificar Identidade'}
                  {etapa === 3 && 'Criar Nova Senha'}
                </h2>
                <p className="text-gray-600 text-sm sm:text-base text-center">
                  {etapa === 1 && 'Informe seu CPF ou CNPJ cadastrado'}
                  {etapa === 2 && 'Digite o código enviado para seus contatos'}
                  {etapa === 3 && 'Escolha uma senha forte para sua conta'}
                </p>
              </div>

              {/* ── ETAPA 1 ── */}
              {etapa === 1 && (
                <form onSubmit={handleSubmitEtapa1} className="space-y-4 sm:space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Tipo de documento</label>
                    <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
                      {(['cpf', 'cnpj'] as const).map((tipo) => (
                        <button
                          key={tipo}
                          type="button"
                          onClick={() => handleTipoDocChange(tipo)}
                          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                            tipoDoc === tipo ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          <i className={tipo === 'cpf' ? 'ri-user-line text-base' : 'ri-building-line text-base'}></i>
                          {tipo.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="login" className="block text-sm font-semibold text-gray-700 mb-2">
                      {tipoDoc === 'cpf' ? 'CPF' : 'CNPJ'}
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <i className="ri-id-card-line text-lg text-gray-400"></i>
                      </div>
                      <input
                        id="login"
                        type="text"
                        inputMode="numeric"
                        value={login}
                        onChange={(e) => handleLoginChange(e.target.value)}
                        onBlur={() => { if (login.trim()) validateLogin(); }}
                        className={`block w-full pl-12 pr-4 py-3.5 border ${error ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'} rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent focus:bg-white transition-all text-sm tracking-wide`}
                        placeholder={tipoDoc === 'cpf' ? '000.000.000-00' : '00.000.000/0000-00'}
                        maxLength={tipoDoc === 'cpf' ? 14 : 18}
                      />
                    </div>
                    {error && (
                      <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                        <i className="ri-error-warning-line"></i>{error}
                      </p>
                    )}
                  </div>

                  {/* Seletor de canal */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Como deseja receber o código?
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setCanalEnvio('email')}
                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all cursor-pointer ${
                          canalEnvio === 'email'
                            ? 'border-orange-500 bg-orange-50 text-orange-700'
                            : 'border-gray-200 bg-gray-50 text-gray-500 hover:border-gray-300 hover:bg-gray-100'
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${canalEnvio === 'email' ? 'bg-orange-100' : 'bg-gray-100'}`}>
                          <i className={`ri-mail-line text-xl ${canalEnvio === 'email' ? 'text-orange-600' : 'text-gray-400'}`}></i>
                        </div>
                        <div className="text-center">
                          <p className={`text-sm font-semibold ${canalEnvio === 'email' ? 'text-orange-700' : 'text-gray-600'}`}>E-mail</p>
                          <p className={`text-xs mt-0.5 ${canalEnvio === 'email' ? 'text-orange-500' : 'text-gray-400'}`}>{contatoMascarado.email}</p>
                        </div>
                        {canalEnvio === 'email' && (
                          <div className="w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center">
                            <i className="ri-check-line text-white text-xs"></i>
                          </div>
                        )}
                      </button>

                      <button
                        type="button"
                        onClick={() => setCanalEnvio('sms')}
                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all cursor-pointer ${
                          canalEnvio === 'sms'
                            ? 'border-orange-500 bg-orange-50 text-orange-700'
                            : 'border-gray-200 bg-gray-50 text-gray-500 hover:border-gray-300 hover:bg-gray-100'
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${canalEnvio === 'sms' ? 'bg-orange-100' : 'bg-gray-100'}`}>
                          <i className={`ri-smartphone-line text-xl ${canalEnvio === 'sms' ? 'text-orange-600' : 'text-gray-400'}`}></i>
                        </div>
                        <div className="text-center">
                          <p className={`text-sm font-semibold ${canalEnvio === 'sms' ? 'text-orange-700' : 'text-gray-600'}`}>SMS</p>
                          <p className={`text-xs mt-0.5 ${canalEnvio === 'sms' ? 'text-orange-500' : 'text-gray-400'}`}>{contatoMascarado.celular}</p>
                        </div>
                        {canalEnvio === 'sms' && (
                          <div className="w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center">
                            <i className="ri-check-line text-white text-xs"></i>
                          </div>
                        )}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3.5 px-6 rounded-xl font-semibold text-sm hover:from-orange-600 hover:to-orange-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                  >
                    {isLoading ? (
                      <><i className="ri-loader-4-line text-xl animate-spin"></i>Enviando...</>
                    ) : (
                      <>
                        <i className={`${canalEnvio === 'email' ? 'ri-mail-send-line' : 'ri-message-2-line'} text-xl`}></i>
                        Enviar código por {canalEnvio === 'email' ? 'E-mail' : 'SMS'}
                      </>
                    )}
                  </button>

                  {/* Erro da API */}
                  {apiError && (
                    <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
                      <i className="ri-error-warning-fill text-red-500 text-lg flex-shrink-0 mt-0.5"></i>
                      <div className="flex-1">
                        <p className="text-sm text-red-700 leading-relaxed font-medium">{apiError}</p>
                        {apiErrorDetails.length > 0 && (
                          <ul className="mt-2 space-y-1">
                            {apiErrorDetails.map((detail, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm text-red-600">
                                <i className="ri-close-circle-line mt-0.5 flex-shrink-0 text-red-400"></i>
                                <span>{detail}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="relative py-3">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200"></div></div>
                    <div className="relative flex justify-center text-sm"><span className="px-4 bg-white text-gray-500">ou</span></div>
                  </div>

                  <button
                    type="button"
                    onClick={() => navigate('/premiado/login')}
                    className="w-full bg-white border-2 border-gray-200 text-gray-700 py-3.5 px-6 rounded-xl font-semibold text-sm hover:border-orange-500 hover:text-orange-600 hover:bg-orange-50 transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-line text-xl"></i>Voltar para o Login
                  </button>
                </form>
              )}

              {/* ── ETAPA 2 — TOKEN ── */}
              {etapa === 2 && (
                <>
                  {/* ── TELA DE BLOQUEIO ── */}
                  {bloqueado ? (
                    <div className="space-y-5">
                      {/* Ícone de bloqueio animado */}
                      <div className="flex flex-col items-center gap-3 py-2">
                        <div className="relative">
                          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
                            <i className="ri-lock-2-fill text-4xl text-red-500"></i>
                          </div>
                          <div className="absolute -top-1 -right-1 w-7 h-7 bg-red-500 rounded-full flex items-center justify-center">
                            <i className="ri-close-line text-white text-sm font-bold"></i>
                          </div>
                        </div>
                        <div className="text-center">
                          <h3 className="text-lg font-bold text-red-700">Acesso Temporariamente Bloqueado</h3>
                          <p className="text-sm text-gray-500 mt-1">Muitas tentativas incorretas detectadas</p>
                        </div>
                      </div>

                      {/* Contador circular */}
                      <div className="bg-red-50 border border-red-200 rounded-2xl p-5 text-center">
                        <p className="text-xs text-red-500 font-semibold uppercase tracking-wider mb-3">
                          Desbloqueio automático em
                        </p>
                        <div className="inline-flex items-center justify-center w-24 h-24 rounded-full border-4 border-red-300 bg-white mb-3">
                          <span className="text-2xl font-bold text-red-600 tabular-nums">
                            {formatBloqueioTime(bloqueioCountdown)}
                          </span>
                        </div>
                        {/* Barra de progresso */}
                        <div className="w-full bg-red-100 rounded-full h-2 mt-1">
                          <div
                            className="bg-red-400 h-2 rounded-full transition-all duration-1000"
                            style={{ width: `${(bloqueioCountdown / BLOQUEIO_SEGUNDOS) * 100}%` }}
                          />
                        </div>
                        <p className="text-xs text-red-400 mt-2">
                          {Math.ceil(bloqueioCountdown / 60)} min restante{Math.ceil(bloqueioCountdown / 60) !== 1 ? 's' : ''}
                        </p>
                      </div>

                      {/* Motivo */}
                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2">
                        <div className="flex items-start gap-2">
                          <i className="ri-information-line text-slate-500 text-base mt-0.5 flex-shrink-0"></i>
                          <div>
                            <p className="text-sm font-semibold text-slate-700 mb-1">Por que fui bloqueado?</p>
                            <p className="text-xs text-slate-500 leading-relaxed">
                              Você atingiu o limite de <strong>{MAX_TENTATIVAS} tentativas incorretas</strong> de verificação. Por segurança, o acesso foi temporariamente suspenso para proteger sua conta.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Dica */}
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                        <div className="flex items-start gap-2">
                          <i className="ri-lightbulb-line text-amber-500 text-base mt-0.5 flex-shrink-0"></i>
                          <p className="text-xs text-amber-700">
                            Verifique sua caixa de entrada ou mensagens SMS. O código expira em 10 minutos após o envio. Após o desbloqueio, um novo código será enviado automaticamente.
                          </p>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          setEtapa(1);
                          setToken(['', '', '', '', '', '']);
                          setTokenError('');
                          setBloqueado(false);
                          setBloqueioCountdown(0);
                          setTentativas(0);
                        }}
                        className="w-full bg-white border-2 border-gray-200 text-gray-700 py-3 px-6 rounded-xl font-semibold text-sm hover:border-red-400 hover:text-red-600 hover:bg-red-50 transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-arrow-left-line text-xl"></i>Voltar ao início
                      </button>
                    </div>
                  ) : (
                    /* ── FORMULÁRIO NORMAL ── */
                    <form onSubmit={handleVerifyToken} className="space-y-5">
                      {/* Identidade confirmada */}
                      <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-3">
                        <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-check-line text-white text-sm"></i>
                        </div>
                        <div>
                          <p className="text-xs text-green-700 font-semibold">Identidade localizada</p>
                          <p className="text-xs text-green-600">{login}</p>
                        </div>
                      </div>

                      {/* Onde foi enviado */}
                      <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <i className={`${canalEnvio === 'email' ? 'ri-mail-check-line' : 'ri-message-2-line'} text-xl text-orange-600`}></i>
                          </div>
                          <div>
                            <p className="text-xs text-orange-600 font-semibold uppercase tracking-wide mb-0.5">
                              Código enviado via {canalEnvio === 'email' ? 'E-mail' : 'SMS'}
                            </p>
                            <p className="text-sm font-semibold text-orange-800">
                              {canalEnvio === 'email' ? contatoMascarado.email : contatoMascarado.celular}
                            </p>
                            <button
                              type="button"
                              onClick={() => { setEtapa(1); setToken(['', '', '', '', '', '']); setTokenError(''); setTentativas(0); }}
                              className="text-xs text-orange-500 hover:text-orange-700 underline underline-offset-1 mt-0.5 cursor-pointer"
                            >
                              Trocar canal de envio
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Indicador de tentativas */}
                      {tentativas > 0 && (
                        <div className={`rounded-xl p-3 border flex items-center gap-3 ${
                          tentativas === MAX_TENTATIVAS - 1
                            ? 'bg-red-50 border-red-200'
                            : 'bg-amber-50 border-amber-200'
                        }`}>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            tentativas === MAX_TENTATIVAS - 1 ? 'bg-red-100' : 'bg-amber-100'
                          }`}>
                            <i className={`ri-alarm-warning-line text-base ${
                              tentativas === MAX_TENTATIVAS - 1 ? 'text-red-600' : 'text-amber-600'
                            }`}></i>
                          </div>
                          <div className="flex-1">
                            <p className={`text-xs font-semibold ${
                              tentativas === MAX_TENTATIVAS - 1 ? 'text-red-700' : 'text-amber-700'
                            }`}>
                              {tentativas === MAX_TENTATIVAS - 1
                                ? 'Última tentativa! Próximo erro bloqueia o acesso.'
                                : `Tentativa ${tentativas} de ${MAX_TENTATIVAS}`}
                            </p>
                            {/* Bolinhas de tentativas */}
                            <div className="flex gap-1.5 mt-1.5">
                              {Array.from({ length: MAX_TENTATIVAS }).map((_, i) => (
                                <div
                                  key={i}
                                  className={`w-2.5 h-2.5 rounded-full transition-all ${
                                    i < tentativas
                                      ? tentativas === MAX_TENTATIVAS - 1 ? 'bg-red-500' : 'bg-amber-500'
                                      : 'bg-gray-200'
                                  }`}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Campos do token */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3 text-center">
                          Digite o código de 6 dígitos
                        </label>
                        <div className="flex gap-2 justify-center" onPaste={handleTokenPaste}>
                          {token.map((digit, idx) => (
                            <input
                              key={idx}
                              ref={(el) => { tokenRefs.current[idx] = el; }}
                              type="text"
                              inputMode="numeric"
                              maxLength={1}
                              value={digit}
                              onChange={(e) => handleTokenChange(idx, e.target.value)}
                              onKeyDown={(e) => handleTokenKeyDown(idx, e)}
                              className={`w-11 h-14 text-center text-xl font-bold border-2 rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                                tokenError
                                  ? tentativas === MAX_TENTATIVAS - 1
                                    ? 'border-red-400 bg-red-50 text-red-700'
                                    : 'border-amber-300 bg-amber-50 text-amber-700'
                                  : digit
                                  ? 'border-orange-400 bg-orange-50 text-orange-700'
                                  : 'border-gray-200 bg-gray-50 text-gray-800'
                              }`}
                            />
                          ))}
                        </div>
                        {tokenError && (
                          <p className={`mt-3 text-sm flex items-center justify-center gap-1 ${
                            tentativas === MAX_TENTATIVAS - 1 ? 'text-red-600' : 'text-amber-600'
                          }`}>
                            <i className="ri-error-warning-line"></i>{tokenError}
                          </p>
                        )}
                      </div>

                      {/* Reenviar */}
                      <div className="text-center">
                        <p className="text-sm text-gray-500 mb-1">
                          Não recebeu o código {canalEnvio === 'email' ? 'no e-mail' : 'por SMS'}?
                        </p>
                        {resendCooldown > 0 ? (
                          <p className="text-sm text-gray-400">
                            Reenviar em <span className="font-semibold text-orange-500">{resendCooldown}s</span>
                          </p>
                        ) : (
                          <button
                            type="button"
                            onClick={handleResend}
                            className="text-sm font-semibold text-orange-600 hover:text-orange-700 underline underline-offset-2 cursor-pointer"
                          >
                            Reenviar por {canalEnvio === 'email' ? 'e-mail' : 'SMS'}
                          </button>
                        )}
                      </div>

                      {/* Sem acesso ao contato */}
                      <div className="border-t border-gray-100 pt-4">
                        <button
                          type="button"
                          onClick={() => setShowSemAcessoInfo(!showSemAcessoInfo)}
                          className="w-full flex items-center justify-between text-sm text-gray-500 hover:text-gray-700 transition-colors cursor-pointer group"
                        >
                          <span className="flex items-center gap-2">
                            <i className="ri-question-line text-base text-gray-400 group-hover:text-gray-600"></i>
                            Não tenho acesso ao {canalEnvio === 'email' ? 'e-mail' : 'celular'} cadastrado
                          </span>
                          {showSemAcessoInfo
                            ? <i className="ri-arrow-up-s-line text-base text-gray-400"></i>
                            : <i className="ri-arrow-down-s-line text-base text-gray-400"></i>
                          }
                        </button>

                        {showSemAcessoInfo && (
                          <div className="mt-3 bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 bg-slate-200 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                                <i className="ri-lock-unlock-line text-slate-600 text-base"></i>
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-slate-800 mb-1">
                                  Recuperação via Suporte
                                </p>
                                <p className="text-xs text-slate-600 leading-relaxed">
                                  Se você não tem mais acesso ao {canalEnvio === 'email' ? 'e-mail' : 'celular'} cadastrado, nossa equipe pode verificar sua identidade e atualizar seus dados de contato com segurança.
                                </p>
                              </div>
                            </div>

                            <div className="space-y-2">
                              {[
                                { icon: 'ri-user-search-line', text: 'Verificação de identidade por documento' },
                                { icon: 'ri-shield-check-line', text: 'Processo seguro e auditado' },
                                { icon: 'ri-time-line', text: 'Atendimento em até 1 dia útil' },
                              ].map((item) => (
                                <div key={item.text} className="flex items-center gap-2 text-xs text-slate-600">
                                  <i className={`${item.icon} text-slate-400 text-sm flex-shrink-0`}></i>
                                  <span>{item.text}</span>
                                </div>
                              ))}
                            </div>

                            <button
                              type="button"
                              onClick={() => setShowTicketModal(true)}
                              className="w-full flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-800 text-white py-2.5 px-4 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-ticket-line text-base"></i>
                              Abrir chamado no suporte
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Dica */}
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                        <div className="flex items-start gap-2">
                          <i className="ri-lightbulb-line text-amber-500 text-base mt-0.5 flex-shrink-0"></i>
                          <p className="text-xs text-amber-700">
                            O código expira em 10 minutos após o envio. Verifique sua caixa de entrada ou mensagens SMS.
                          </p>
                        </div>
                      </div>

                      <button
                        type="submit"
                        disabled={isVerifying || token.join('').length < 6}
                        className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3.5 px-6 rounded-xl font-semibold text-sm hover:from-orange-600 hover:to-orange-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                      >
                        {isVerifying ? (
                          <><i className="ri-loader-4-line text-xl animate-spin"></i>Verificando...</>
                        ) : (
                          <><i className="ri-shield-check-line text-xl"></i>Verificar Código</>
                        )}
                      </button>

                      {/* Erro da API na etapa 2 */}
                      {apiError && (
                        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
                          <i className="ri-error-warning-fill text-red-500 text-lg flex-shrink-0 mt-0.5"></i>
                          <div className="flex-1">
                            <p className="text-sm text-red-700 leading-relaxed font-medium">{apiError}</p>
                            {apiErrorDetails.length > 0 && (
                              <ul className="mt-2 space-y-1">
                                {apiErrorDetails.map((detail, idx) => (
                                  <li key={idx} className="flex items-start gap-2 text-sm text-red-600">
                                    <i className="ri-close-circle-line mt-0.5 flex-shrink-0 text-red-400"></i>
                                    <span>{detail}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={() => { setEtapa(1); setToken(['', '', '', '', '', '']); setTokenError(''); setTentativas(0); setApiError(''); setApiErrorDetails([]); }}
                        className="w-full bg-white border-2 border-gray-200 text-gray-700 py-3 px-6 rounded-xl font-semibold text-sm hover:border-orange-500 hover:text-orange-600 hover:bg-orange-50 transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-arrow-left-line text-xl"></i>Voltar
                      </button>
                    </form>
                  )}
                </>
              )}

              {/* ── ETAPA 3 — NOVA SENHA ── */}
              {etapa === 3 && (
                <form onSubmit={handleSubmitEtapa3} className="space-y-4 sm:space-y-5">
                  {/* Verificação confirmada */}
                  <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-shield-check-line text-white text-sm"></i>
                    </div>
                    <div>
                      <p className="text-xs text-green-700 font-semibold">Identidade verificada com sucesso</p>
                      <p className="text-xs text-green-600">Agora crie sua nova senha</p>
                    </div>
                  </div>

                  {/* Nova Senha */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Nova Senha</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <i className="ri-lock-line text-lg text-gray-400"></i>
                      </div>
                      <input
                        type={showNovaSenha ? 'text' : 'password'}
                        value={novaSenha}
                        onChange={(e) => { setNovaSenha(e.target.value); if (errosSenha.novaSenha) setErrosSenha({ ...errosSenha, novaSenha: '' }); }}
                        placeholder="Mínimo 8 caracteres"
                        className={`block w-full pl-12 pr-12 py-3.5 border ${errosSenha.novaSenha ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'} rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent focus:bg-white transition-all text-sm`}
                      />
                      <button type="button" onClick={() => setShowNovaSenha(!showNovaSenha)} className="absolute inset-y-0 right-0 pr-4 flex items-center cursor-pointer">
                        <i className={`${showNovaSenha ? 'ri-eye-off-line' : 'ri-eye-line'} text-lg text-gray-400 hover:text-gray-600`}></i>
                      </button>
                    </div>
                    {novaSenha && (
                      <div className="mt-2">
                        <div className="flex gap-1 mb-1">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <div key={n} className={`h-1.5 flex-1 rounded-full transition-all ${n <= forcaSenha.nivel ? forcaSenha.cor : 'bg-gray-200'}`} />
                          ))}
                        </div>
                        <p className={`text-xs font-medium ${forcaSenha.nivel <= 2 ? 'text-red-500' : forcaSenha.nivel === 3 ? 'text-yellow-600' : 'text-green-600'}`}>
                          {forcaSenha.label}
                        </p>
                      </div>
                    )}
                    {errosSenha.novaSenha && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <i className="ri-error-warning-line"></i>{errosSenha.novaSenha}
                      </p>
                    )}
                    <p className="mt-1.5 text-xs text-gray-500">Use ao menos 8 caracteres, uma letra maiúscula e um número</p>
                  </div>

                  {/* Confirmar Senha */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Confirmar Nova Senha</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <i className="ri-lock-2-line text-lg text-gray-400"></i>
                      </div>
                      <input
                        type={showConfirmarSenha ? 'text' : 'password'}
                        value={confirmarSenha}
                        onChange={(e) => { setConfirmarSenha(e.target.value); if (errosSenha.confirmarSenha) setErrosSenha({ ...errosSenha, confirmarSenha: '' }); }}
                        placeholder="Repita a nova senha"
                        className={`block w-full pl-12 pr-12 py-3.5 border ${errosSenha.confirmarSenha ? 'border-red-300 bg-red-50' : confirmarSenha && novaSenha === confirmarSenha ? 'border-green-400 bg-green-50' : 'border-gray-200 bg-gray-50'} rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent focus:bg-white transition-all text-sm`}
                      />
                      <button type="button" onClick={() => setShowConfirmarSenha(!showConfirmarSenha)} className="absolute inset-y-0 right-0 pr-4 flex items-center cursor-pointer">
                        <i className={`${showConfirmarSenha ? 'ri-eye-off-line' : 'ri-eye-line'} text-lg text-gray-400 hover:text-gray-600`}></i>
                      </button>
                    </div>
                    {confirmarSenha && novaSenha === confirmarSenha && (
                      <p className="mt-1 text-sm text-green-600 flex items-center gap-1">
                        <i className="ri-check-line"></i>Senhas coincidem
                      </p>
                    )}
                    {errosSenha.confirmarSenha && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <i className="ri-error-warning-line"></i>{errosSenha.confirmarSenha}
                      </p>
                    )}
                  </div>

                  <div className="flex gap-3 pt-1">
                    <button
                      type="button"
                      onClick={() => { setEtapa(2); setNovaSenha(''); setConfirmarSenha(''); setErrosSenha({}); setApiError(''); setApiErrorDetails([]); }}
                      className="flex-1 bg-white border-2 border-gray-200 text-gray-700 py-3.5 px-4 rounded-xl font-semibold text-sm hover:border-orange-500 hover:text-orange-600 hover:bg-orange-50 transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-arrow-left-line"></i>Voltar
                    </button>
                    <button
                      type="submit"
                      disabled={isSaving}
                      className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3.5 px-4 rounded-xl font-semibold text-sm hover:from-orange-600 hover:to-orange-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                    >
                      {isSaving ? (
                        <><i className="ri-loader-4-line animate-spin"></i>Salvando...</>
                      ) : (
                        <><i className="ri-check-line"></i>Salvar Senha</>
                      )}
                    </button>
                  </div>

                  {/* Erro da API na etapa 3 */}
                  {apiError && (
                    <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
                      <i className="ri-error-warning-fill text-red-500 text-lg flex-shrink-0 mt-0.5"></i>
                      <div className="flex-1">
                        <p className="text-sm text-red-700 leading-relaxed font-medium">{apiError}</p>
                        {apiErrorDetails.length > 0 && (
                          <ul className="mt-2 space-y-1">
                            {apiErrorDetails.map((detail, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm text-red-600">
                                <i className="ri-close-circle-line mt-0.5 flex-shrink-0 text-red-400"></i>
                                <span>{detail}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  )}
                </form>
              )}
            </div>

            {/* Footer */}
            <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-slate-200">
              <div className="flex items-center justify-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                <img
                  src="https://static.readdy.ai/image/22e92c18f0b031d861a5e53bf8e362fb/6356cf72c36f3ef424056fffdf6d6f31.png"
                  alt="GetPoints Logo"
                  className="h-8 sm:h-10 w-auto object-contain"
                />
              </div>
              <p className="text-center text-xs sm:text-sm text-slate-600 mb-2 sm:mb-3 px-4">
                © 2025 GetPoints Tecnologia, C.R.I.E. Ltda. Todos os direitos reservados.
              </p>
              <div className="flex items-center justify-center gap-3 sm:gap-4 text-xs sm:text-sm flex-wrap">
                <a href="/premiado/login" className="text-slate-600 hover:text-orange-600 transition-colors">Termos de Uso</a>
                <span className="text-slate-300">•</span>
                <a href="/premiado/login" className="text-slate-600 hover:text-orange-600 transition-colors">Política de Privacidade</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Ticket de Suporte */}
      {showTicketModal && (
        <TicketModal
          isOpen={showTicketModal}
          onClose={() => setShowTicketModal(false)}
          defaultCategory="cadastro"
          defaultPriority="alta"
          defaultSubject="Sem acesso ao contato cadastrado — Recuperação de senha"
          defaultDescription={`Olá, não consigo receber o código de verificação pois não tenho mais acesso ao ${canalEnvio === 'email' ? 'e-mail' : 'celular'} cadastrado na minha conta.\n\nDocumento informado: ${login}\n\nSolicito atualização do contato de verificação para que eu possa redefinir minha senha.`}
        />
      )}

      {/* Modal de Sucesso */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8">
            <div className="flex justify-center mb-4 sm:mb-6">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-100 rounded-full flex items-center justify-center">
                <i className="ri-shield-check-line text-3xl sm:text-4xl text-green-600"></i>
              </div>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-800 text-center mb-2 sm:mb-3">Senha Redefinida!</h3>
            <p className="text-sm sm:text-base text-gray-600 text-center mb-4 sm:mb-6">
              Sua senha foi atualizada com sucesso. Agora você pode fazer login com a nova senha.
            </p>
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
              <div className="flex items-start gap-2">
                <i className="ri-information-line text-orange-600 text-base sm:text-lg mt-0.5 flex-shrink-0"></i>
                <p className="text-xs sm:text-sm text-orange-800">
                  Por segurança, todas as sessões ativas foram encerradas. Faça login novamente.
                </p>
              </div>
            </div>
            <button
              onClick={handleCloseModal}
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-xl font-semibold text-sm sm:text-base hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Ir para o Login
            </button>
          </div>
        </div>
      )}
    </>
  );
}
